/**
 * File Upload Utility Functions
 *
 * Provides validation and error handling helpers for file uploads across the application.
 */

import { formatLanguageName } from './composerLanguages';

/**
 * Default maximum file size in MB
 */
export const DEFAULT_MAX_FILE_SIZE_MB = 100;

/**
 * File size limits for different file types (in MB)
 */
export const FILE_SIZE_LIMITS = {
  image: 100,
  video: 100,
  audio: 100,
  document: 100,
  default: 100
};

/**
 * Validates file size against a maximum limit
 *
 * @param {File} file - The file to validate
 * @param {number} maxSizeMB - Maximum file size in megabytes
 * @returns {Object} - { valid: boolean, error: string|null }
 */
export const validateFileSize = (file, maxSizeMB = DEFAULT_MAX_FILE_SIZE_MB) => {
  if (!file) {
    return { valid: false, error: 'No file selected' };
  }

  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      error: `File size exceeds ${maxSizeMB}MB limit. Please select a smaller file.`
    };
  }

  return { valid: true, error: null };
};

/**
 * Validates file type against accepted types
 *
 * @param {File} file - The file to validate
 * @param {string[]} acceptedTypes - Array of accepted MIME types or extensions (e.g., ['image/*', '.pdf'])
 * @returns {Object} - { valid: boolean, error: string|null }
 */
export const validateFileType = (file, acceptedTypes = []) => {
  if (!file) {
    return { valid: false, error: 'No file selected' };
  }

  if (!acceptedTypes || acceptedTypes.length === 0) {
    return { valid: true, error: null };
  }

  const fileType = file.type;
  const fileName = file.name;

  for (const acceptedType of acceptedTypes) {
    // Check wildcard MIME types (e.g., 'image/*')
    if (acceptedType.includes('*')) {
      const prefix = acceptedType.split('/*')[0];
      if (fileType.startsWith(prefix + '/')) {
        return { valid: true, error: null };
      }
    }
    // Check specific MIME types
    else if (acceptedType === fileType) {
      return { valid: true, error: null };
    }
    // Check file extensions
    else if (acceptedType.startsWith('.') && fileName.toLowerCase().endsWith(acceptedType.toLowerCase())) {
      return { valid: true, error: null };
    }
  }

  return {
    valid: false,
    error: `Invalid file type. Please select a valid file (${acceptedTypes.join(', ')}).`
  };
};

export const extractServerErrorMessage = (error) => {
  const responseData = error?.response?.data;
  const nestedError = responseData?.error;

  if (typeof nestedError === 'string' && nestedError.trim()) {
    return nestedError.trim();
  }

  if (typeof nestedError?.message === 'string' && nestedError.message.trim()) {
    return nestedError.message.trim();
  }

  if (typeof responseData?.message === 'string' && responseData.message.trim()) {
    return responseData.message.trim();
  }

  if (typeof responseData?.detail === 'string' && responseData.detail.trim()) {
    return responseData.detail.trim();
  }

  return null;
};

/**
 * Removes Pydantic / framework noise often appended to API error strings (not meant for end users).
 */
export const stripPydanticNoiseFromText = (text) => {
  if (text == null || typeof text !== 'string') return '';
  let s = text;
  const typeIdx = s.search(/\s*\[type=/i);
  if (typeIdx >= 0) s = s.slice(0, typeIdx);
  const furIdx = s.search(/\s*For further information visit\s+/i);
  if (furIdx >= 0) s = s.slice(0, furIdx);
  return s.trim();
};

/**
 * Turns coach JSON paths like /0/goals/0/description into readable labels for tooltips.
 */
const describeCoachContentPathForTooltip = (path = '') => {
  const parts = String(path || '')
    .split('/')
    .filter(Boolean);
  if (!parts.length) return 'Content';

  const labels = [];
  let index = 0;

  if (/^\d+$/.test(parts[0])) {
    labels.push(`Sub-step ${Number(parts[0]) + 1}`);
    index = 1;
  }

  while (index < parts.length) {
    const part = parts[index];
    const nextPart = parts[index + 1];

    if (part === 'goals') {
      if (/^\d+$/.test(nextPart || '')) {
        labels.push(`goal ${Number(nextPart) + 1}`);
        index += 2;
        continue;
      }
      labels.push('goals');
      index += 1;
      continue;
    }

    labels.push(String(part).replace(/_/g, ' '));
    index += 1;
  }

  return labels.join(' ');
};

/**
 * Shortens long Pydantic/coach validation blobs from the API into one clear sentence when possible.
 */
export const shortenCoachContentValidationMessage = (text) => {
  const trimmed = stripPydanticNoiseFromText(String(text || ''));
  const atRe = /\(\s*at\s+([^)]+)\s*\)/i;
  const atMatch = atRe.exec(trimmed);
  if (!atMatch) return null;

  const beforeAt = trimmed.slice(0, atMatch.index).trim();
  const path = atMatch[1].trim();
  const location = describeCoachContentPathForTooltip(path);

  let detail = beforeAt
    .replace(/^\d+\s+validation errors?\s+for\s+[\w.]+\s*/i, '')
    .replace(/^Value error,\s*/i, '')
    .replace(/^Content validation error:\s*(?:\d+\.\s*)?/i, '')
    .replace(/\s*Please fix these issues before saving this active lesson step\.?$/i, '')
    .trim();

  if (/^Field cannot be empty/i.test(detail)) {
    return `${location} is empty. Fill it in before saving this active step.`;
  }
  if (/^Missing required field/i.test(detail)) {
    return `${location} is missing required information.`;
  }
  if (/^Expected .+?, but found/i.test(detail) || /^Invalid value/i.test(detail)) {
    return `${location} is invalid. Check the value and try again.`;
  }
  if (/^Array must contain at least one item/i.test(detail)) {
    if (/goals$/i.test(location)) return `${location}: add at least one goal.`;
    return `${location}: add at least one item.`;
  }
  if (/^Unexpected field/i.test(detail)) {
    return `${location} has a field that is not allowed. Remove it or check the format.`;
  }

  if (detail && detail.length < 200) {
    return `${location}: ${detail}`;
  }
  return `${location} needs to be fixed before saving.`;
};

/**
 * Cleans API/axios strings for save-button tooltips (plain language, no jargon).
 */
export const humanizeSaveTooltipText = (text) => {
  if (text == null) return '';
  let s = String(text).replace(/\s+/g, ' ').trim();
  if (!s) return '';

  s = stripPydanticNoiseFromText(s);

  s = s.replace(/^Request failed with status code \d+\s*[.:]?\s*/i, '');
  s = s.replace(/^Error:\s*/i, '');
  s = s.replace(/^AxiosError:\s*/i, '');

  if (/^Network Error$/i.test(s)) {
    return '';
  }
  if (/timeout/i.test(s) && /(exceeded|timed out|abort)/i.test(s)) {
    return 'The request took too long. Check your connection and try again.';
  }

  return s;
};

const looksLikeGenericAxiosMessage = (message) => {
  if (!message || typeof message !== 'string') return true;
  const m = message.trim();
  if (!m) return true;
  if (/^Request failed with status code \d+$/i.test(m)) return true;
  if (/^Network Error$/i.test(m)) return true;
  return false;
};

const friendlyHttpStatusSaveMessage = (status) => {
  if (status === 401 || status === 403) {
    return "You don't have permission to save. Try signing in again, or ask an administrator.";
  }
  if (status === 404) {
    return "That content wasn't found. It may have been deleted. Refresh the page and try again.";
  }
  if (status === 408) {
    return 'The request took too long. Check your connection and try again.';
  }
  if (status === 409) {
    return 'Someone else may have changed this content. Refresh the page and try saving again.';
  }
  if (status === 412) {
    return 'Your screen is out of date. Refresh the page, then try saving again.';
  }
  if (status === 413) {
    return 'That file is too large. Use a smaller file or ask an administrator.';
  }
  if (status === 429) {
    return 'Too many attempts. Wait a moment and try again.';
  }
  if (status === 400 || status === 422) {
    return 'Some information is missing or not valid. Check your entries and try again.';
  }
  if (status === 502 || status === 503 || status === 504) {
    return 'The service is temporarily unavailable. Try again in a few minutes.';
  }
  if (status >= 500 && status <= 599) {
    return 'Something went wrong on our side. Please try again in a moment.';
  }
  return null;
};

/**
 * Should a failed save stop the debounced autosave from re-sending the same payload?
 *
 * Re-sending only helps when the failure was transient. A 4xx means the server refused
 * *this* payload and will keep refusing it — 404 / 409 / 412 above literally tell the
 * user to refresh — so a 3s retry loop cannot recover and just hammers the API until the
 * tab is closed. A 5xx or a timeout is worth retrying.
 *
 * Callers key the block on the payload/snapshot, so any edit releases it.
 *
 * @param {unknown} error
 * @returns {boolean} true to stop autosave until the content changes
 */
export const shouldBlockAutosaveAfterSaveError = (error) => {
  const status = error?.response?.status;
  if (typeof status === 'number') {
    if (status === 408) return false;
    return status >= 400 && status <= 499;
  }
  // No response at all — network drop, CORS, abort.
  return error instanceof Error && typeof error?.message === 'string';
};

/**
 * The nested error object from this project's standard envelope: `{ error: { code, message, details } }`.
 *
 * @param {unknown} error - an axios error
 * @returns {{code?: string, message?: string, details?: object}|null}
 */
export const extractServerErrorPayload = (error) => {
  const nested = error?.response?.data?.error;
  if (!nested || typeof nested !== 'object' || Array.isArray(nested)) return null;
  return nested;
};

const asNonEmptyStrings = (values) =>
  (Array.isArray(values) ? values : [])
    .map((value) => (value == null ? '' : String(value).trim()))
    .filter(Boolean);

/**
 * Render a `{ [language]: [item, …] }` failure map as one line per FAILURE.
 *
 * Deliberately not one line per language with the items joined: the editors split the formatted
 * message back into one bullet per problem, and a joined line ("English: A; B") lost the language
 * label off everything after the first separator. Repeating the language per line costs a few
 * characters and keeps every bullet independently readable.
 */
const linesFromLanguageMap = (map) => {
  if (!map || typeof map !== 'object' || Array.isArray(map)) return [];
  return Object.keys(map)
    .sort()
    .flatMap((language) =>
      asNonEmptyStrings(map[language]).map((item) => `${formatLanguageName(language)}: ${item}`)
    );
};

/**
 * Turn a server error's structured `details` into readable lines.
 *
 * The API answers a refused save with the precise reason — which requirement failed, in which
 * language — under `error.details`. Rendering `error.message` alone would surface a blocked save as
 * "Cannot activate this item. (ACTIVATION_INCOMPLETE)", with no way to learn that (say) the Hebrew
 * provider name was empty. This reads the detail shapes the API actually emits:
 *
 * - `missing_labels_by_language` — per-language requirement LABELS (preferred)
 * - `missing_by_language` — the same failures as bare KEYS; the fallback, and what an older
 *   server or the user-info usage guard sends
 * - `global_errors` — language-independent failures, already human sentences
 * - `unresolved_placeholders` — from the classification-prompt builder
 *
 * @param {unknown} error - an axios error
 * @returns {string[]|null} readable lines, or null when there is nothing structured to show
 */
export const describeServerErrorDetails = (error) => {
  const details = extractServerErrorPayload(error)?.details;
  if (!details || typeof details !== 'object' || Array.isArray(details)) return null;

  const lines = [];

  // Prefer labels ("Course provider name is required") over keys ("provider_name"), but keep
  // rendering keys when that is all the server sent — a bare key still beats no reason at all.
  const labelled = details.missing_labels_by_language;
  const hasLabels = labelled && typeof labelled === 'object' && Object.keys(labelled).length > 0;
  lines.push(...linesFromLanguageMap(hasLabels ? labelled : details.missing_by_language));

  lines.push(...asNonEmptyStrings(details.global_errors));

  const unresolved = asNonEmptyStrings(details.unresolved_placeholders);
  if (unresolved.length) {
    lines.push(`Unresolved placeholders: ${unresolved.join(', ')}`);
  }

  return lines.length ? lines : null;
};

export const formatApiError = (error, defaultMessage = 'Something went wrong. Please try again.') => {
  if (!error) {
    return humanizeSaveTooltipText(defaultMessage) || defaultMessage;
  }

  // Structured details first: they name the actual blocker, and every branch below this collapses
  // the failure to a single generic sentence. `userMessage` in particular is set by the axios
  // interceptor for EVERY 422, so leaving this any lower would keep the real reason unreachable.
  const detailLines = describeServerErrorDetails(error);
  if (detailLines) {
    const payload = extractServerErrorPayload(error);
    const heading = humanizeSaveTooltipText(payload?.message || '');
    // When the server composed its message FROM these details, keeping both repeats every reason —
    // and that heading packs them into ONE line behind separators, which the editors' bullet split
    // would then tear apart mid-reason. The lines alone say the same thing, one bullet each.
    // A heading that is NOT derived (e.g. the user-info usage guard) carries context of its own.
    const headingIsDerived = detailLines.some((line) => heading.includes(line));
    const composed = (headingIsDerived ? detailLines : [heading, ...detailLines]).filter(Boolean);
    if (composed.length) return composed.join('\n');
  }

  if (typeof error.userMessage === 'string' && error.userMessage.trim()) {
    const rawUser = error.userMessage.trim();
    const coachFromUser = shortenCoachContentValidationMessage(rawUser);
    if (coachFromUser) return coachFromUser;
    const u = humanizeSaveTooltipText(rawUser);
    return u || rawUser;
  }

  const status = error?.response?.status;
  const friendlyStatus = friendlyHttpStatusSaveMessage(status);

  if (!error.response) {
    const code = error.code;
    if (code === 'ECONNABORTED' || /timeout/i.test(String(error.message || ''))) {
      return 'The request took too long. Check your connection and try again.';
    }
    if (looksLikeGenericAxiosMessage(error.message)) {
      return "Can't reach the server. Check your internet connection and try again.";
    }
    const m = humanizeSaveTooltipText(error.message);
    return m || "Can't reach the server. Check your internet connection and try again.";
  }

  const serverMessage = extractServerErrorMessage(error);
  if (serverMessage && !looksLikeGenericAxiosMessage(serverMessage)) {
    const coachShort = shortenCoachContentValidationMessage(serverMessage);
    if (coachShort) return coachShort;
    const h = humanizeSaveTooltipText(serverMessage);
    if (h) return h;
  }

  if (friendlyStatus) return friendlyStatus;

  if (!looksLikeGenericAxiosMessage(error.message)) {
    const coachFromMsg = shortenCoachContentValidationMessage(error.message);
    if (coachFromMsg) return coachFromMsg;
    const m = humanizeSaveTooltipText(error.message);
    if (m) return m;
  }

  return humanizeSaveTooltipText(defaultMessage) || defaultMessage;
};

/** Strip a trailing machine error code — the `(ACTIVATION_INCOMPLETE)` the axios interceptor appends. */
export const stripErrorCodeSuffix = (line) =>
  String(line == null ? '' : line)
    .replace(/\s*\([A-Z][A-Z0-9_]*\)\s*$/, '')
    .trim();

/**
 * Split a formatted save error into the lines an editor should list.
 *
 * A multi-language activation failure arrives newline-separated, and its per-language lines
 * legitimately contain semicolons ("Hebrew: name is required; description is required") — so
 * splitting a multi-line message on ";" would strip the language off every requirement after the
 * first. Newlines win when present; the semicolon split stays for single-line messages.
 *
 * @param {unknown} message - a formatted error (string) or an already-structured value
 * @returns {string[]} trimmed, non-empty lines
 */
export const splitSaveErrorLines = (message) => {
  if (Array.isArray(message)) {
    return message.map((line) => String(line ?? '').trim()).filter(Boolean);
  }
  if (typeof message !== 'string') {
    return message == null ? [] : [message];
  }
  const separator = message.includes('\n') ? /\n+/ : /;/;
  return message
    .split(separator)
    .map((line) => line.trim())
    .filter(Boolean);
};

const friendlySaveSubjectPhrase = (subject) => {
  const s = String(subject || '').trim().toLowerCase();
  if (!s || s === 'request') return 'your changes';
  if (s === 'lesson' || s === 'the lesson') return 'this lesson';
  if (s === 'lesson step' || s === 'lesson steps') return 'this step';
  if (s === 'unit') return 'this unit';
  if (s === 'lesson version') return 'this lesson version';
  if (s === 'content' || s === 'coach content') return 'content';
  if (s === 'content order' || s === 'coach content order') return 'content order';
  if (s === 'changes' || s === 'your changes') return 'your changes';
  return subject;
};

export const buildActionFallback = (
  action = 'complete',
  subject = 'request',
  fallbackMessage = null
) => {
  const normalizedAction = typeof action === 'string' ? action.trim().toLowerCase() : '';
  const normalizedSubject = typeof subject === 'string' ? subject.trim() : '';

  if (fallbackMessage) {
    return fallbackMessage;
  }

  if (normalizedAction === 'save') {
    const phrase = friendlySaveSubjectPhrase(normalizedSubject);
    return `We couldn't save ${phrase}. Please try again.`;
  }

  if (normalizedAction && normalizedSubject) {
    return `Unable to ${normalizedAction} ${normalizedSubject}. Please try again.`;
  }

  if (normalizedAction) {
    return `Unable to ${normalizedAction}. Please try again.`;
  }

  return 'Something went wrong. Please try again.';
};

export const formatActionError = (
  error,
  action = 'complete',
  subject = 'request',
  fallbackMessage = null
) => {
  // The editor cancelled (a delete they were asked about): nothing went wrong, so there is nothing to say.
  if (error?.cancelled) return null;
  const defaultMessage = buildActionFallback(action, subject, fallbackMessage);

  return formatApiError(error, defaultMessage);
};

export const formatDeleteError = (error, subject = 'item') => {
  return formatActionError(error, 'delete', subject);
};

export const formatLoadError = (error, subject = 'data') =>
  formatActionError(error, 'load', subject);

export const formatSaveError = (error, subject = 'changes') =>
  formatActionError(error, 'save', subject);

export const formatReorderError = (error, subject = 'items') =>
  formatActionError(error, 'save', `${subject} order`);

export const formatImportError = (error, subject = 'item') =>
  formatActionError(error, 'import', subject);

export const formatExportError = (error, subject = 'item') =>
  formatActionError(error, 'export', subject);

/**
 * Human message for a warn-only export that finished but couldn't bundle some assets (the file
 * still downloaded; those fields kept their original URL). Pair with downloadContentExport's onWarning.
 *
 * @param {{count?: number, names?: string[]}} warning
 * @returns {string}
 */
export const formatExportWarning = ({ count = 0, names = [] } = {}) => {
  const n = Number(count) || (Array.isArray(names) ? names.length : 0);
  const noun = n === 1 ? 'asset' : 'assets';
  const sample =
    Array.isArray(names) && names.length
      ? ` (${names.slice(0, 5).join(', ')}${names.length > 5 ? '…' : ''})`
      : '';
  return `Export finished, but ${n} ${noun} couldn't be bundled${sample}. Those items kept their original links.`;
};

export const formatGenerateError = (error, subject = 'content') =>
  formatActionError(error, 'generate', subject);

export const formatRefineError = (error, subject = 'content') =>
  formatActionError(error, 'refine', subject);

export const formatEvaluateError = (error, subject = 'item') =>
  formatActionError(error, 'evaluate', subject);

export const formatMoveError = (error, subject = 'item') =>
  formatActionError(error, 'move', subject);

export const formatUpdateError = (error, subject = 'item') =>
  formatActionError(error, 'update', subject);

/**
 * Formats upload error based on HTTP status code
 *
 * @param {Object} error - Axios error object
 * @param {string} defaultMessage - Default error message if no specific match
 * @returns {string} - User-friendly error message
 */
export const formatUploadError = (error, defaultMessage = 'Failed to upload file') => {
  if (!error) {
    return defaultMessage;
  }

  // Handle 413 - Request Entity Too Large
  if (error.response?.status === 413) {
    return 'File size exceeds server limit. Please contact your administrator to increase the upload size limit, or select a smaller file.';
  }

  const formattedApiError = formatApiError(error, defaultMessage);
  if (formattedApiError !== defaultMessage) {
    return formattedApiError;
  }

  // Handle 400 - Bad Request
  if (error.response?.status === 400) {
    return 'Invalid file. Please check the file and try again.';
  }

  // Handle 500 - Internal Server Error
  if (error.response?.status === 500) {
    return 'Server error occurred during upload. Please try again or contact support.';
  }

  // Handle network errors
  if (!error.response) {
    return 'Network error. Please check your connection and try again.';
  }

  // Handle other errors
  return error.message || defaultMessage;
};

/**
 * Validates a file before upload
 *
 * @param {File} file - The file to validate
 * @param {Object} options - Validation options
 * @param {number} options.maxSizeMB - Maximum file size in MB
 * @param {string[]} options.acceptedTypes - Array of accepted MIME types or extensions
 * @returns {Object} - { valid: boolean, error: string|null }
 */
export const validateFile = (file, options = {}) => {
  const { maxSizeMB = DEFAULT_MAX_FILE_SIZE_MB, acceptedTypes = [] } = options;

  if (!file) {
    return { valid: false, error: 'No file selected' };
  }

  // Validate file size
  const sizeValidation = validateFileSize(file, maxSizeMB);
  if (!sizeValidation.valid) {
    return sizeValidation;
  }

  // Validate file type
  const typeValidation = validateFileType(file, acceptedTypes);
  if (!typeValidation.valid) {
    return typeValidation;
  }

  return { valid: true, error: null };
};

/**
 * Helper to handle file upload with validation
 *
 * @param {File} file - The file to upload
 * @param {Function} uploadFn - The upload function to call if validation passes
 * @param {Object} options - Validation and error handling options
 * @param {number} options.maxSizeMB - Maximum file size in MB
 * @param {string[]} options.acceptedTypes - Array of accepted MIME types or extensions
 * @param {Function} options.onError - Error callback function
 * @param {string} options.errorMessage - Default error message
 * @returns {Promise} - Result of the upload function or null if validation fails
 */
export const handleFileUpload = async (file, uploadFn, options = {}) => {
  const {
    maxSizeMB = DEFAULT_MAX_FILE_SIZE_MB,
    acceptedTypes = [],
    onError = null,
    errorMessage = 'Failed to upload file'
  } = options;

  // Validate file
  const validation = validateFile(file, { maxSizeMB, acceptedTypes });
  if (!validation.valid) {
    if (onError) {
      onError(validation.error);
    }
    return null;
  }

  // Attempt upload
  try {
    return await uploadFn(file);
  } catch (error) {
    const formattedError = formatUploadError(error, errorMessage);
    if (onError) {
      onError(formattedError);
    }
    throw error;
  }
};

/* =========================================================================
 * Content-bundle export/import helpers
 *
 * Media-bearing content (lessons, lesson steps, scenarios, units, courses, info pages,
 * skills, all-skills) can be exported as a self-contained ZIP bundle (content.json +
 * manifest.json + assets/) by adding `?bundle=zip`. Media-less endpoints (classification
 * questions, interactive tools, skill groups) ignore the param and return plain JSON.
 * Import accepts a `.zip` bundle, an unzipped folder, or a legacy `.json` file.
 * ========================================================================= */

/** Max size (MB) for an imported bundle — bundles can be much larger than legacy JSON. */
export const BUNDLE_IMPORT_MAX_MB = 200;

/** Imports, exports, deletes and bundle updates share the server proxy's ten-minute timeout. */
export const LONG_REQUEST_TIMEOUT_MS = 600000;

/** `accept` attribute for bundle-or-JSON file inputs. */
export const BUNDLE_IMPORT_ACCEPT = '.zip,.json,application/zip,application/json';

/** `acceptedTypes` for validateFile() on bundle-or-JSON imports. */
export const BUNDLE_IMPORT_ACCEPTED_TYPES = ['.zip', '.json', 'application/zip', 'application/json'];

const parseContentDispositionFilename = (disposition = '') => {
  if (!disposition) return null;
  // RFC 5987 `filename*=UTF-8''name` takes precedence over a plain `filename="name"`.
  const star = /filename\*=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  if (star && star[1]) {
    try {
      return decodeURIComponent(star[1].trim());
    } catch (e) {
      return star[1].trim();
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(disposition);
  return plain && plain[1] ? plain[1].trim() : null;
};

/**
 * Resolve the filename to save a downloaded response as: prefer the server's
 * `Content-Disposition` (case-insensitive), falling back to `fallback` when the header
 * is absent. The server is the single source of export filenames (it stamps the
 * environment + timestamp), so client download handlers should route through this.
 *
 * @param {import('axios').AxiosResponse} response
 * @param {string} fallback - filename used only if the header is missing
 * @returns {string}
 */
export const getResponseDownloadFilename = (response, fallback) => {
  const headers = response?.headers || {};
  const disposition = headers['content-disposition'] || headers['Content-Disposition'] || '';
  return parseContentDispositionFilename(disposition) || fallback;
};

/** Save `blob` in the browser as a download named `filename`. */
export const saveBlobAsDownload = (blob, filename) => {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
};

/**
 * Download a content export. By default requests a ZIP bundle (`?bundle=zip`); media-less
 * endpoints ignore that and return JSON. The saved filename is taken from the response's
 * Content-Disposition (so the extension is `.zip` or `.json` as appropriate), falling back
 * to `fallbackName`.
 *
 * @param {import('axios').AxiosInstance} apiClient
 * @param {string} url - export endpoint (without the bundle query param)
 * @param {string} fallbackName - filename to use if Content-Disposition is absent
 * @param {Object} [opts]
 * @param {boolean} [opts.bundle=true] - append `?bundle=zip`
 * @param {(warning: {count: number, names: string[]}) => void} [opts.onWarning] - called when the
 *   bundle downloaded but some referenced assets couldn't be fetched (warn-only export policy). The
 *   file still saved; those fields keep their original URL. Callers that omit it are unaffected.
 * @returns {Promise<string>} the filename the browser was told to save
 */
export const downloadContentExport = async (apiClient, url, fallbackName, opts = {}) => {
  const { bundle = true, onWarning } = opts;
  let requestUrl = url;
  if (bundle) {
    requestUrl += (url.includes('?') ? '&' : '?') + 'bundle=zip';
  }
  const response = await apiClient.get(requestUrl, {
    responseType: 'blob',
    timeout: LONG_REQUEST_TIMEOUT_MS,
  });
  const headers = response.headers || {};
  const getHeader = (name) => headers[name] || headers[name.toLowerCase()] || '';
  const filename = parseContentDispositionFilename(getHeader('Content-Disposition')) || fallbackName;
  const data = response.data;
  const blob = data instanceof Blob ? data : new Blob([data]);
  saveBlobAsDownload(blob, filename);

  // Warn-only export: if the server couldn't bundle some assets, surface them (the bundle still
  // downloaded — those fields keep their original URL). Callers opt in by passing onWarning.
  if (typeof onWarning === 'function') {
    const count = parseInt(getHeader('X-Bundle-Unresolved-Count'), 10);
    if (count > 0) {
      const rawNames = getHeader('X-Bundle-Unresolved-Names');
      const names = rawNames ? rawNames.split(',').map((s) => s.trim()).filter(Boolean) : [];
      onWarning({ count, names });
    }
  }
  return filename;
};

/** True if the selected file is a ZIP bundle (vs a legacy `.json` export). */
export const isZipFile = (file) => {
  if (!file) return false;
  const name = (file.name || '').toLowerCase();
  return (
    name.endsWith('.zip') ||
    file.type === 'application/zip' ||
    file.type === 'application/x-zip-compressed'
  );
};

/**
 * Validates a folder (unzipped bundle) upload: its files go up in one request, so together they are held to the
 * cap a single bundle file is.
 *
 * @param {FileList|File[]} files
 * @param {number} maxSizeMB - Maximum total size in MB
 * @returns {Object} - { valid: boolean, error: string|null }
 */
export const validateFolderSize = (files, maxSizeMB) => {
  const total = Array.from(files || []).reduce((sum, file) => sum + (file.size || 0), 0);
  if (total > maxSizeMB * 1024 * 1024) {
    return {
      valid: false,
      error: `Folder size exceeds ${maxSizeMB}MB limit. Please select a smaller folder.`
    };
  }
  return { valid: true, error: null };
};

/**
 * Build multipart FormData for a folder (unzipped bundle) upload from a `webkitdirectory`
 * FileList / File[]. Each file is appended under `files`, with a parallel `relative_paths`
 * JSON array — the server basenames uploaded filenames, so the relative path of each file
 * must be sent explicitly (read_import_payload's folder branch maps these to the bundle).
 *
 * @param {FileList|File[]} files
 * @param {Object} [extraFields] - extra form fields (e.g. dependency_resolutions)
 * @returns {FormData}
 */
export const buildFolderImportFormData = (files, extraFields = {}) => {
  const formData = new FormData();
  const relativePaths = [];
  Array.from(files || []).forEach((file) => {
    formData.append('files', file);
    relativePaths.push(file.webkitRelativePath || file.name);
  });
  formData.append('relative_paths', JSON.stringify(relativePaths));
  Object.entries(extraFields || {}).forEach(([key, value]) => {
    formData.append(key, value);
  });
  return formData;
};

/**
 * Build multipart FormData for an import from `source`: one file (a `.json` or a ZIP, sent as it is under `file`)
 * or a folder's files (an array, sent as `buildFolderImportFormData` sends them), with `extraFields` beside it.
 *
 * @param {File|File[]} source
 * @param {Object} [extraFields] - extra form fields (e.g. skill_group_id)
 * @returns {FormData}
 */
export const buildImportFormData = (source, extraFields = {}) => {
  if (Array.isArray(source)) return buildFolderImportFormData(source, extraFields);
  const formData = new FormData();
  formData.append('file', source);
  Object.entries(extraFields || {}).forEach(([key, value]) => {
    formData.append(key, value);
  });
  return formData;
};

/**
 * Import a lesson step from `source` (a file, or a folder's files) at the end of a lesson version.
 *
 * @param {import('axios').AxiosInstance} apiClient
 * @param {number|string} versionId
 * @param {File|File[]} source
 * @returns {Promise<import('axios').AxiosResponse>} the response, whose data is the new step
 */
export const importLessonStep = (apiClient, versionId, source) => apiClient.post(
  `/backoffice/v1/lesson-versions/${versionId}/import-step/`, buildImportFormData(source),
  { headers: { 'Content-Type': 'multipart/form-data' }, timeout: LONG_REQUEST_TIMEOUT_MS },
);
